import { useState, type FormEvent } from 'react';
import {
  DEFAULT_HOURS,
  MAX_HOURS,
  MIN_HOURS,
  formatHoursValue,
  parseHours,
  secondsToHours,
  stepHours,
} from '@shared/domain/hours.ts';
import { localDate } from '@shared/domain/time.ts';
import { store, useApp, type HoursSheetState } from '../state/store.ts';
import { formatElapsed } from '../lib/format.ts';
import { SheetDialog, useNow } from './common.tsx';

const toText = (hours: number) => String(hours).replace('.', ',');

/** Hoja de horas: iniciar o detener el cronómetro, o registrar el total de un día. */
export function HoursSheet() {
  const { hoursSheet } = useApp();
  if (!hoursSheet) return null;
  // Cada apertura parte de cero (fecha, selector y texto).
  const key = [hoursSheet.cardId, hoursSheet.date, hoursSheet.userId, hoursSheet.withTimer];
  return <Sheet key={key.join('|')} sheet={hoursSheet} />;
}

function Sheet({ sheet }: { sheet: HoursSheetState }) {
  // myLogs y logsByCard: el total del día se relee cuando cambian.
  const { cards, details, userId } = useApp();
  const card = details[sheet.cardId] ?? cards.find((c) => c.id === sheet.cardId);
  const today = localDate(new Date());
  const target = sheet.userId ?? userId ?? '';
  const totalOf = (day: string) => {
    const log = store.logFor(sheet.cardId, day, target);
    return log ? secondsToHours(log.seconds) : null;
  };

  const [date, setDate] = useState(sheet.date ?? today);
  const [hours, setHours] = useState(totalOf(sheet.date ?? today) ?? DEFAULT_HOURS);
  // Editar otro día abre directo el registro específico (con la fecha a la vista).
  const [free, setFree] = useState(Boolean(sheet.date && sheet.date !== today));
  const [custom, setCustom] = useState(toText(hours));
  const [busy, setBusy] = useState(false);
  const close = () => store.closeHoursSheet();

  const existing = totalOf(date);
  const parsed = free ? parseHours(custom) : ({ ok: true, hours } as const);
  const value = parsed.ok ? parsed.hours : null;
  const cta =
    value === null
      ? 'Registrar'
      : `${existing === null ? 'Registrar' : 'Guardar'} ${formatHoursValue(value)}`;

  function changeDate(next: string) {
    if (!next || next > today) return;
    setDate(next);
    const total = totalOf(next) ?? DEFAULT_HOURS;
    setHours(total);
    setCustom(toText(total));
  }

  function toggleFree() {
    if (free) {
      // Volver al selector deja la fecha de hoy y su total.
      const total = totalOf(today) ?? DEFAULT_HOURS;
      setDate(today);
      setHours(total);
    } else {
      setCustom(toText(hours));
    }
    setFree(!free);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (value === null || busy) return;
    setBusy(true);
    const ok = await store.setWorkLog({
      cardId: sheet.cardId,
      date,
      hours: value,
      userId: sheet.userId,
    });
    setBusy(false);
    if (ok) close();
  }

  return (
    <SheetDialog
      title={sheet.withTimer ? 'Horas en esta tarjeta' : 'Registrar horas'}
      subtitle={card?.name || '(sin nombre)'}
      onClose={close}
      testId="hours-sheet"
    >
      {sheet.withTimer && card && <TimerChoice cardId={card.id} closed={card.is_closed} />}
      {sheet.withTimer && card && (
        <p className="sheet-divider" aria-hidden="true">
          o registra lo que ya hiciste
        </p>
      )}

      <form className="sheet-form" onSubmit={(e) => void save(e)}>
        {existing !== null && (
          <p className="muted small" data-testid="hours-existing">
            {date === today ? 'Hoy llevas' : 'Ese día llevas'} {formatHoursValue(existing)} en esta
            tarjeta.
          </p>
        )}
        {!free ? (
          <div className="stepper" role="group" aria-label="Horas trabajadas">
            <button
              type="button"
              className="stepper-button"
              aria-label="Menos horas"
              disabled={hours <= MIN_HOURS}
              onClick={() => setHours((h) => stepHours(h, -1))}
            >
              −
            </button>
            <output className="stepper-value" aria-live="polite">
              {formatHoursValue(hours)}
            </output>
            <button
              type="button"
              className="stepper-button"
              aria-label="Más horas"
              disabled={hours >= MAX_HOURS}
              onClick={() => setHours((h) => stepHours(h, 1))}
            >
              +
            </button>
          </div>
        ) : (
          <div className="sheet-free">
            <label>
              Horas trabajadas
              <span className="hours-input">
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={custom}
                  aria-invalid={!parsed.ok && parsed.error !== null}
                  onChange={(e) => setCustom(e.target.value)}
                />
                <span aria-hidden="true">h</span>
              </span>
            </label>
            {!parsed.ok && parsed.error && (
              <p className="form-error" role="alert">
                {parsed.error}
              </p>
            )}
            <label>
              Fecha del trabajo
              <input
                type="date"
                value={date}
                max={today}
                onChange={(e) => changeDate(e.target.value)}
              />
            </label>
          </div>
        )}
        <button type="button" className="link-button sheet-toggle" onClick={toggleFree}>
          {free ? 'Volver al selector' : 'Especificar horas y fecha'}
        </button>
        <button
          type="submit"
          className="button button-primary sheet-submit"
          disabled={value === null || busy}
        >
          {cta}
        </button>
      </form>
    </SheetDialog>
  );
}

/** Iniciar el cronómetro en la tarjeta, o detenerlo si ya corre aquí. */
function TimerChoice({ cardId, closed }: { cardId: string; closed: boolean }) {
  const { openEntry, cards, details } = useApp();
  const now = useNow(1000);
  const card = details[cardId] ?? cards.find((c) => c.id === cardId);
  if (openEntry?.card_id === cardId) {
    return (
      <button
        type="button"
        className="button button-danger sheet-submit"
        onClick={() => {
          store.closeHoursSheet();
          void store.stopTimer();
        }}
      >
        Detener cronómetro ·{' '}
        {formatElapsed((now.getTime() - Date.parse(openEntry.started_at)) / 1000)}
      </button>
    );
  }
  const other = openEntry ? (cards.find((c) => c.id === openEntry.card_id)?.name ?? null) : null;
  return (
    <div className="sheet-timer">
      <button
        type="button"
        className="button button-primary sheet-submit"
        disabled={closed}
        onClick={() => {
          store.closeHoursSheet();
          if (card) void store.startTimer(card);
        }}
      >
        Iniciar cronómetro
      </button>
      {closed ? (
        <p className="muted small">
          El cronómetro no está disponible en tarjetas cerradas. Puedes registrar las horas.
        </p>
      ) : (
        other && (
          <p className="muted small">Iniciar detiene «{other}» y suma ese tiempo a su registro.</p>
        )
      )}
    </div>
  );
}
